import {
  Injectable,
  inject,
  signal,
  computed,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from '@angular/fire/firestore';
import {
  Functions,
  httpsCallable,
} from '@angular/fire/functions';
import { Branch } from '../models/branch.model';
import { AccessService } from './access.service';
import { describeFirestoreError } from '../utils/firebase-error';

/**
 * Manages the Firestore `branches` collection.
 *
 * Reads are direct Firestore queries (filtered by rules).
 * Branch **creation** goes through the `createBranch` callable
 * so the server can seed BUSINESS members into the new branch.
 */
@Injectable({ providedIn: 'root' })
export class BranchService {
  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private injector = inject(Injector);
  private accessService = inject(AccessService);
  private readonly COLLECTION = 'branches';

  loading = signal(false);
  error = signal<string | null>(null);
  branches = signal<Branch[]>([]);

  totalBranches = computed(() => this.branches().length);

  /**
   * Load branches by ID list for a business. Use for non-SuperAdmin so they
   * only fetch branches they belong to (from membership mirror), avoiding
   * collection-query rules or index issues.
   */
  async getBranchesByIds(
    businessId: string,
    branchIds: string[],
  ): Promise<Branch[]> {
    if (branchIds.length === 0) {
      this.branches.set([]);
      return [];
    }
    this.loading.set(true);
    this.error.set(null);
    try {
      return await runInInjectionContext(this.injector, async () => {
        const snaps = await Promise.all(
          branchIds.map((id) =>
            getDoc(doc(this.firestore, this.COLLECTION, id)),
          ),
        );
        const items = snaps
          .filter((s) => s.exists() && s.data()?.['businessId'] === businessId)
          .map((s) => ({ id: s.id, ...s.data() } as Branch))
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
        this.branches.set(items);
        return items;
      });
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, 'Failed to fetch branches');
      this.error.set(message);
      console.error('Failed to fetch branches:', err);
      return [];
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * The branches of a business that the caller may see.
   *
   * The scoping lives HERE, not in callers. A branch-only OWNER is not a member
   * of every branch, so the `where('businessId')` query below includes rows the
   * per-branch rule refuses, the whole query is denied and the list comes back
   * empty — known issue §9/§B, and audit #11 found it again on three screens
   * added after those were fixed. Only SuperAdmin runs the collection query;
   * everyone else reads their branches by id from the membership mirror, which
   * is what the mirror exists for.
   */
  async getBranches(businessId: string): Promise<Branch[]> {
    if (!this.accessService.isSuperAdmin()) {
      return this.getBranchesByIds(
        businessId,
        this.accessService.getAccessibleBranchIds(businessId),
      );
    }
    this.loading.set(true);
    this.error.set(null);

    try {
      return await runInInjectionContext(this.injector, async () => {
        const ref = collection(this.firestore, this.COLLECTION);
        const q = query(
          ref,
          where('businessId', '==', businessId),
          orderBy('name', 'asc'),
          limit(100),
        );
        const snapshot = await getDocs(q);

        const items = snapshot.docs.map(
          (d) => ({ id: d.id, ...d.data() }) as Branch,
        );
        this.branches.set(items);
        return items;
      });
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, 'Failed to fetch branches');
      this.error.set(message);
      console.error('Failed to fetch branches:', err);
      return [];
    } finally {
      this.loading.set(false);
    }
  }

  async getBranch(id: string): Promise<Branch | null> {
    try {
      return await runInInjectionContext(this.injector, async () => {
        const docRef = doc(this.firestore, this.COLLECTION, id);
        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) return null;
        return { id: snapshot.id, ...snapshot.data() } as Branch;
      });
    } catch (err: unknown) {
      console.error('Failed to fetch branch:', err);
      return null;
    }
  }

  /**
   * Create a branch via the Cloud Function callable.
   * The callable creates the branch doc and seeds BUSINESS members.
   */
  async createBranch(
    businessId: string,
    name: string,
  ): Promise<string | null> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const result = await runInInjectionContext(this.injector, async () => {
        const callable = httpsCallable<
          { businessId: string; name: string },
          { success: boolean; branchId: string }
        >(this.functions, 'createBranch');
        return await callable({ businessId, name });
      });
      // The new id is named explicitly: the callable seeds the caller's
      // membership, but the mirror listener may not have delivered it yet, and
      // a refresh that reads only the mirror would omit the branch just made.
      if (this.accessService.isSuperAdmin()) {
        await this.getBranches(businessId);
      } else {
        await this.getBranchesByIds(businessId, [
          ...new Set([
            ...this.accessService.getAccessibleBranchIds(businessId),
            result.data.branchId,
          ]),
        ]);
      }
      return result.data.branchId;
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, 'Failed to create branch');
      this.error.set(message);
      console.error('Failed to create branch:', err);
      return null;
    } finally {
      this.loading.set(false);
    }
  }
}
